import React, { useEffect, useRef, useState } from "react";
import { Typography, Box, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import moment from "moment";
import Swal from "sweetalert2";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "./MyDraftServiceRequests.css";
import { complaintAPI } from "../services/api";
import { escapeHtml, escapeHtmlAttr } from "../utils/xss";
import { useSidebar } from "../contexts/SidebarContext";
import { hasUserType } from "../utils/auth";

/** Left-align all headers for the My Draft grid only (main table + scrollX clone). */
const applyMyDraftTableLeftHeaders = (api) => {
  if (!api) return;
  const alignHeader = ($th) => {
    $th.addClass("text-start").css("text-align", "left");
    $th.find(".dt-column-header, .dt-column-title").css({
      textAlign: "left",
      justifyContent: "flex-start",
    });
  };
  api.columns().every(function () {
    alignHeader($(this.header()));
  });
  const $wrapper = $(api.table().node()).closest(".dataTables_wrapper, .dt-container");
  $wrapper.find(".dt-scroll-head thead th").each(function () {
    alignHeader($(this));
  });
};

const MyDraftServiceRequests = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const draftTableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const { sidebarOpen } = useSidebar();
  const [drafts, setDrafts] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Get current user ID from localStorage
  const getCurrentUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user.userId || user.id || 1; // Default to 1 if not found
    } catch {
      return 1;
    }
  };

  // Get current user code for filtering drafts
  const getCurrentUserCode = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user.userIdCode || user.userCode || user.email || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadDrafts = async () => {
      setLoading(true);
      try {
        // Get drafts from API (pass null for createdBy so backend uses token - matches how draft was saved)
        const response = await complaintAPI.getComplaintDrafts(null, 1, 100);
        
        if (!mounted) return;

        // API returns { data: { drafts, totalRecords, ... } } - support both camelCase and PascalCase
        const payload = response?.data ?? response?.Data ?? {};
        const draftsData = payload.drafts ?? payload.Drafts ?? [];
        setDrafts(draftsData);

        // Ensure datatable plugin registered
        if (!$.fn.DataTable) {
          DataTable($);
        }

        // Destroy previous instance
        if (dataTableInstanceRef.current) {
          dataTableInstanceRef.current.destroy();
          dataTableInstanceRef.current = null;
        }

        dataTableInstanceRef.current = $(draftTableRef.current).DataTable({
            data: draftsData,
            paging: true,
            searching: true,
            info: true,
            lengthChange: true,
            lengthMenu: [5, 10, 25, 50, 100],
            pageLength: 10,
            dom: "lfrtip",
            ordering: true,
            destroy: true,
            scrollX: true,
            autoWidth: false,
            columnDefs: [
              { targets: "_all", className: "text-start" },
              { targets: 8, width: "165px", minWidth: 165 },  // Created On - date + time
              { targets: 9, width: "165px", minWidth: 165 },  // Modified On - date + time
            ],
            columns: [
              {
                title: "SL.No.",
                data: null,
                render: (d, type, row, meta) => (meta.row + 1).toString(),
              },
              {
                title: "Draft Code",
                data: null,
                render: (d) => {
                  const srCode = d.SRCode || d.srCode || "";
                  if (!srCode) return "—";
                  const href = `/raise-complaint?srCode=${encodeURIComponent(srCode)}`;
                  return `<a href="${escapeHtmlAttr(href)}" class="draft-invoice-link" data-srcode="${escapeHtmlAttr(srCode)}" style="color: #1976d2; font-weight: 600; text-decoration: underline;">${escapeHtml(srCode)}</a>`;
                },
              },
              {
                title: "Invoice Number",
                data: null,
                render: (d) => d.InvoiceNumber || d.invoiceNumber || "",
              },
              {
                title: "ODN Number",
                data: null,
                render: (d) => d.ODNNumber || d.odnNumber || "",
              },
              {
                title: "Customer ID",
                data: null,
                render: (d) => d.CustomerID || d.customerID || "",
              },
              {
                title: "Customer Name",
                data: null,
                render: (d) => d.CustomerName || d.customerName || "",
              },
              {
                title: "Customer Mobile No.",
                data: null,
                render: (d) => d.CustomerPhone || d.customerPhone || "",
              },
              {
                title: "Business Vertical",
                data: null,
                render: (d) => d.BusinessVertical || d.businessVertical || "",
              },
              {
                title: "Created On",
                data: null,
                render: (d) => {
                  const created = d.CreatedOn ?? d.createdOn;
                  if (created == null || created === "") return "N/A";
                  const m = moment(created, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"], true);
                  return m.isValid() ? m.format("DD-MM-YYYY HH:mm:ss") : String(created);
                },
              },
              {
                title: "Modified On",
                data: null,
                render: (d) => {
                  const modified = d.LastSavedDate ?? d.lastSavedDate ?? d.ModifiedOn ?? d.modifiedOn;
                  if (modified == null || modified === "") return "N/A";
                  const m = moment(modified, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"], true);
                  return m.isValid() ? m.format("DD-MM-YYYY HH:mm:ss") : String(modified);
                },
              },
              {
                title: "Action",
                data: null,
                orderable: false,
                searchable: false,
                render: (d) => {
                  const srCode = d.SRCode || d.srCode || "";
                  return `
                    <div class="d-flex justify-content-start gap-2">
                      <button class="btn btn-sm resume-draft" data-srcode="${srCode}" title="Resume Draft"
                        style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); 
                               border: none; 
                               color: white; 
                               padding: 8px 10px; 
                               border-radius: 6px; 
                               box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
                               transition: all 0.3s ease;
                               font-weight: 500;
                               display: flex;
                               align-items: center;
                               justify-content: center;
                               min-width: 36px;
                               height: 36px;">
                        <i class="bi bi-pencil-fill"></i>
                      </button>
                      <button class="btn btn-sm delete-draft" data-srcode="${srCode}" title="Delete Draft"
                        style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); 
                               border: none; 
                               color: white; 
                               padding: 8px 10px; 
                               border-radius: 6px; 
                               box-shadow: 0 2px 8px rgba(245, 87, 108, 0.4);
                               transition: all 0.3s ease;
                               font-weight: 500;
                               display: flex;
                               align-items: center;
                               justify-content: center;
                               min-width: 36px;
                               height: 36px;">
                        <i class="bi bi-trash-fill"></i>
                      </button>
                    </div>
                  `;
                },
              },
            ],
            initComplete: function (settings, json) {
              try {
                applyMyDraftTableLeftHeaders(this.api());

                // Wrap dt-length and dt-search into a single top filter wrapper
                const wrapper = $(draftTableRef.current).closest(
                  ".dataTables_wrapper, .dt-container"
                );
                if (wrapper.length > 0) {
                  const lengthEl = wrapper.find(".dt-length");
                  const searchEl = wrapper.find(".dt-search");
                  if (lengthEl.length > 0 || searchEl.length > 0) {
                    let topWrap = wrapper.find(".dtFltrTop");
                    if (topWrap.length === 0) {
                      topWrap = $(
                        "<div class='dtFltrTop d-flex flex-wrap'></div>"
                      );
                      wrapper.prepend(topWrap);
                    }
                    lengthEl.each(function () {
                      $(this).appendTo(topWrap);
                    });
                    searchEl.each(function () {
                      $(this).appendTo(topWrap);
                    });
                  }

                  // Wrap dt-info and dt-paging into a single bottom filter wrapper
                  const infoEl = wrapper.find(".dt-info");
                  const pagingEl = wrapper.find(".dt-paging");
                  if (infoEl.length > 0 || pagingEl.length > 0) {
                    let bottomWrap = wrapper.find(".dtFltrBotm");
                    if (bottomWrap.length === 0) {
                      bottomWrap = $(
                        "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                      );
                      wrapper.append(bottomWrap);
                    }
                    infoEl.each(function () {
                      $(this).appendTo(bottomWrap);
                    });
                    pagingEl.each(function () {
                      $(this).appendTo(bottomWrap);
                    });
                  }
                }
              } catch (error) {
                console.error("Error in initComplete:", error);
              }
            },
          });

          // Add hover effects for glossy buttons
          $(draftTableRef.current)
            .off("mouseenter", ".resume-draft, .delete-draft")
            .on("mouseenter", ".resume-draft, .delete-draft", function () {
              $(this).css({
                transform: "translateY(-2px)",
                boxShadow: $(this).hasClass("resume-draft")
                  ? "0 4px 12px rgba(102, 126, 234, 0.6)"
                  : "0 4px 12px rgba(245, 87, 108, 0.6)",
              });
            })
            .off("mouseleave", ".resume-draft, .delete-draft")
            .on("mouseleave", ".resume-draft, .delete-draft", function () {
              $(this).css({
                transform: "translateY(0)",
                boxShadow: $(this).hasClass("resume-draft")
                  ? "0 2px 8px rgba(102, 126, 234, 0.4)"
                  : "0 2px 8px rgba(245, 87, 108, 0.4)",
              });
            })
            .off("mousedown", ".resume-draft, .delete-draft")
            .on("mousedown", ".resume-draft, .delete-draft", function () {
              $(this).css({ transform: "translateY(0)" });
            })
            .off("mouseup", ".resume-draft, .delete-draft")
            .on("mouseup", ".resume-draft, .delete-draft", function () {
              $(this).css({ transform: "translateY(-2px)" });
            });

          // Click handler for Invoice Number link (navigate to raise-complaint with srCode)
          $(draftTableRef.current).off("click", ".draft-invoice-link");
          $(draftTableRef.current).on("click", ".draft-invoice-link", function (e) {
            e.preventDefault();
            const srCode = $(this).attr("data-srcode") || $(this).data("srcode") || "";
            if (srCode) handleResumeDraft(srCode);
          });

          // Click handler for Draft Save Code link (same as Resume)
          $(draftTableRef.current).off("click", ".draft-save-code-link");
          $(draftTableRef.current).on("click", ".draft-save-code-link", function (e) {
            e.preventDefault();
            let srCode = $(this).attr("data-sr-code") || $(this).data("srCode") || "";
            srCode = String(srCode).replace(/&quot;/g, '"').replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
            if (srCode) handleResumeDraft(srCode);
          });

          // Click handler for Resume button
          $(draftTableRef.current).off("click", ".resume-draft");
          $(draftTableRef.current).on("click", ".resume-draft", function (e) {
            e.preventDefault();
            const srCode = $(this).data("srcode");
            handleResumeDraft(srCode);
          });

          // Click handler for Delete button
          $(draftTableRef.current).off("click", ".delete-draft");
          $(draftTableRef.current).on("click", ".delete-draft", function (e) {
            e.preventDefault();
            const srCode = $(this).attr("data-srcode") || $(this).data("srcode") || "";
            const draft = draftsData.find(
              (d) => (d.SRCode || d.srCode) === srCode
            );
            handleDeleteClick(draft || { SRCode: srCode, srCode });
          });
      } catch (err) {
        console.error("Failed to load drafts", err);
        Swal.fire({
          title: "Error",
          text: "Failed to load drafts. Please try again.",
          icon: "error",
          confirmButtonColor: "#ff9800",
        });
        setDrafts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadDrafts();

    return () => {
      mounted = false;
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
      if (draftTableRef.current) {
        $(draftTableRef.current).off("click", ".draft-invoice-link");
        $(draftTableRef.current).off("click", ".draft-save-code-link");
        $(draftTableRef.current).off("click", ".resume-draft");
        $(draftTableRef.current).off("click", ".delete-draft");
        $(draftTableRef.current).off("mouseenter", ".resume-draft, .delete-draft");
        $(draftTableRef.current).off("mouseleave", ".resume-draft, .delete-draft");
        $(draftTableRef.current).off("mousedown", ".resume-draft, .delete-draft");
        $(draftTableRef.current).off("mouseup", ".resume-draft, .delete-draft");
      }
    };
  }, [navigate, refreshKey]);

  // Handle sidebar toggle - recalculate DataTable columns
  useEffect(() => {
    if (dataTableInstanceRef.current) {
      const timer = setTimeout(() => {
        try {
          dataTableInstanceRef.current.columns.adjust().draw(false);
        } catch (error) {
          console.error("Error adjusting DataTable columns:", error);
        }
      }, 350);

      return () => clearTimeout(timer);
    }
  }, [sidebarOpen]);

  const handleResumeDraft = async (srCode) => {
    try {
      setLoading(true);
      
      if (!srCode) {
        Swal.fire({
          title: "Error",
          text: "Draft Save Code not found. Please try again.",
          icon: "error",
          confirmButtonColor: "#ff9800",
        });
        return;
      }

      // Navigate to raise complaint page with SRCode
      // The raise complaint page should load the draft data using SRCode
      navigate(`/raise-complaint?srCode=${encodeURIComponent(srCode)}`);
    } catch (err) {
      console.error("Error resuming draft:", err);
      Swal.fire({
        title: "Error",
        text: "An error occurred while loading the draft. Please try again.",
        icon: "error",
        confirmButtonColor: "#ff9800",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (draft) => {
    const srCode = draft?.SRCode || draft?.srCode;

    if (!srCode) {
      Swal.fire({
        title: "Error",
        text: "Draft Save Code not found.",
        icon: "error",
        confirmButtonColor: "#ff9800",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the draft. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, proceed!",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);

        const response = await complaintAPI.deleteDraftByCode(srCode);
        const success = response?.success ?? response?.Success ?? false;
        const message = response?.message ?? response?.Message ?? (success ? "Draft deleted successfully." : "Failed to delete draft.");

        if (success) {
          Swal.fire({
            title: "Deleted",
            text: message,
            icon: "success",
            confirmButtonColor: "#ff9800",
          });
          setRefreshKey((k) => k + 1);
        } else {
          Swal.fire({
            title: "Error",
            text: message,
            icon: "error",
            confirmButtonColor: "#ff9800",
          });
        }
      } catch (err) {
        console.error("Error deleting draft:", err);
        const errMsg = err.response?.data?.message ?? err.response?.data?.Message ?? err.message ?? "An error occurred while deleting the draft. Please try again.";
        Swal.fire({
          title: "Error",
          text: errMsg,
          icon: "error",
          confirmButtonColor: "#ff9800",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          className="service-request-heading"
          sx={{ color: "#333333", fontWeight: 600 }}
        >
          My Draft Service Requests
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel mt-0">
          <div className="section-heading mb-3">Draft List</div>
          <div className="table-responsive my-draft-sr-grid">
            <table
              ref={draftTableRef}
              className="table table-striped table-bordered complaint-table custmTblLst my-draft-sr-table"
              style={{ width: "100%" }}
            ></table>
          </div>
        </div>
      </Paper>
    </Box>
  );
};

export default MyDraftServiceRequests;

